import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import AddRounded from '@mui/icons-material/AddRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import LayersRounded from '@mui/icons-material/LayersRounded';
import PageHeader from '../components/common/PageHeader';
import ImageUploader from '../components/common/ImageUploader';
import { getFabricCoatings, createFabricCoating, updateFabricCoating, deleteFabricCoating } from '../services/fabricCoatingsApi';

const FabricCoatings = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formName, setFormName] = useState('');
  const [formImage, setFormImage] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const data = await getFabricCoatings();
      setItems(data.items);
    } catch (err) {
      console.error('Failed to fetch fabric coatings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditItem(null);
    setFormName('');
    setFormImage('');
    setFormDesc('');
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setFormName(item.name);
    setFormImage(item.image_url || '');
    setFormDesc(item.description || '');
    setError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      if (editItem) {
        await updateFabricCoating(editItem.id, { name: formName.trim(), image_url: formImage.trim(), description: formDesc.trim() });
      } else {
        await createFabricCoating(formName.trim(), formImage.trim(), formDesc.trim());
      }
      setDialogOpen(false);
      await fetchData();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0]?.message;
      setError(msg || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this fabric coating?')) return;
    setDeleting(id);
    try {
      await deleteFabricCoating(id);
      await fetchData();
    } catch (err) {
      console.error('Failed to delete:', err);
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <Box>
        <PageHeader title="Fabric Coatings" subtitle="Manage special fabric coatings" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Fabric Coatings"
        subtitle="Manage special fabric coatings"
        action={
          <Button variant="contained" startIcon={<AddRounded />} onClick={openCreate}>
            Add Coating
          </Button>
        }
      />

      {items.length === 0 ? (
        <Card>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <LayersRounded sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">No fabric coatings added</Typography>
            <Typography variant="body2" color="text.secondary">Click "Add Coating" to create your first entry</Typography>
          </Box>
        </Card>
      ) : (
        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: 60 }}>Image</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Name</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary', width: 100 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell>
                      <Avatar variant="rounded" src={item.image_url} sx={{ width: 40, height: 40, bgcolor: 'grey.100' }}>
                        <LayersRounded sx={{ fontSize: 20, color: 'grey.400' }} />
                      </Avatar>
                    </TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>{item.name}</Typography></TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(item)} sx={{ color: 'primary.main' }}>
                            <EditRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(item.id)} disabled={deleting === item.id} sx={{ color: 'error.main' }}>
                            {deleting === item.id ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineRounded fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{editItem ? 'Edit Fabric Coating' : 'Add Fabric Coating'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus label="Name" placeholder="e.g. Teflon Coating" value={formName}
            onChange={(e) => { setFormName(e.target.value); setError(''); }}
            fullWidth sx={{ mt: 1, mb: 2 }} error={!!error} helperText={error}
          />
          <Box sx={{ mb: 2 }}>
            <ImageUploader value={formImage} onChange={setFormImage} folder="fabric-coatings" label="Coating Image" />
          </Box>
          <TextField
            label="Description" placeholder="Text description for metafield" value={formDesc}
            onChange={(e) => setFormDesc(e.target.value)}
            fullWidth multiline rows={2} helperText="Will be sent as a separate text metafield"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : (editItem ? 'Save' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FabricCoatings;
